import Backbone from 'backbone';
import React from 'react';
import ReactDOM from 'react-dom/client';

import {CodonTranslationTable} from 'biojs/BioConstants';
import * as biojsUtil from 'biojs/Util';
import BufferTabView from 'buffers/BufferTabView';
import {Sequence} from 'components/Sequence';
import {StrandOfDoubleStranded} from 'constants/MolbioConstants';
import AaSequence from 'molbio/aa-sequences/AaSequence';
import {
  ConnectedAaSequenceBiochemicalProperties,
  TranslationBiochemicalProperties,
} from 'molbio/aa-sequences/BiochemicalProperties';
import {PolymerTabState, PolymerTabStateAttributes} from 'molbio/tabstates/PolymerTabState';
import {TabStateDefaults} from 'Navigation';
import {renderToDOM, unmountRootsAsync} from 'redux-stores/ReduxDOMRender';

const BIOCHEMICAL_PROPERTIES_TAB_STATE_DEFAULTS: Partial<TabStateDefaults> = {
  title: 'Biochemical Properties',
  preferenceKey: 'biochemical-properties',
};

class AaSequenceBiochemicalPropertiesTabState extends PolymerTabState<AaSequence> {
  override defaults(): TabStateDefaults {
    return {
      ...super.defaults(),
      ...BIOCHEMICAL_PROPERTIES_TAB_STATE_DEFAULTS,
      closeable: false,
    };
  }
}

interface TranslationBiochemicalPropertiesTabStateAttributes extends PolymerTabStateAttributes<Sequence> {
  strand: StrandOfDoubleStranded;
  codonTranslationTable: CodonTranslationTable;
  coerceStartCodonToMethionine: boolean;
}

interface TranslationBiochemicalPropertiesTabState {
  get<K extends keyof TranslationBiochemicalPropertiesTabStateAttributes>(
    key: K
  ): TranslationBiochemicalPropertiesTabStateAttributes[K];
  set<K extends keyof TranslationBiochemicalPropertiesTabStateAttributes>(
    attributeName: K,
    value: TranslationBiochemicalPropertiesTabStateAttributes[K],
    options?: Backbone.ModelSetOptions
  ): TranslationBiochemicalPropertiesTabState;
  set(
    obj: Partial<TranslationBiochemicalPropertiesTabStateAttributes>
  ): TranslationBiochemicalPropertiesTabState;
}

class TranslationBiochemicalPropertiesTabState extends PolymerTabState<Sequence> {
  override defaults(): TabStateDefaults {
    return {
      ...super.defaults(),
      ...BIOCHEMICAL_PROPERTIES_TAB_STATE_DEFAULTS,
    };
  }
}

type BiochemicalPropertiesTabState =
  | AaSequenceBiochemicalPropertiesTabState
  | TranslationBiochemicalPropertiesTabState;

class BiochemicalPropertiesTabView extends BufferTabView<BiochemicalPropertiesTabState> {
  reactRoot: ReactDOM.Root | null = null;

  override initialize(): void {
    if (this.model.isSequenceTabState()) {
      this.listenTo(this.model.polymer().selection(), 'change:start change:end', () => this.render());
      this.listenTo(
        this.model,
        'change:strand change:codonTranslationTable change:coerceStartCodonToMethionine',
        () => this.render()
      );
      this.listenTo(this.model.sequence(), 'change:bases', () => this.render());
    }
  }

  override render(): this {
    const model = this.model;

    if (model instanceof AaSequenceBiochemicalPropertiesTabState) {
      this.reactRoot = renderToDOM(
        <ConnectedAaSequenceBiochemicalProperties proteinId={model.protein().id} />,
        this.el,
        this.reactRoot
      );
    } else {
      const [start, end] = model.sequence().selection().getEndpoints();
      let aminoAcids;
      const sequence = model.sequence();
      if (end == null) {
        aminoAcids = '';
      } else {
        aminoAcids = biojsUtil.translateOnStrand(
          sequence.residueSlice(start, end),
          model.get('strand'),
          sequence.nucleotideType(),
          model.get('codonTranslationTable'),
          model.get('coerceStartCodonToMethionine')
        );
      }

      this.reactRoot = renderToDOM(
        <TranslationBiochemicalProperties
          start={start}
          end={end}
          length={sequence.length()}
          startIndex={sequence.startIndex()}
          aminoAcids={aminoAcids}
          sequenceNucleotideType={sequence.nucleotideType()}
          sequenceStrand={model.get('strand')}
          setSequenceStrand={(strand) => model.set('strand', strand)}
          sequenceCodonTranslationTable={model.get('codonTranslationTable')}
          setSequenceCodonTranslationTable={(codonTranslationTable) =>
            model.set('codonTranslationTable', codonTranslationTable)
          }
          coerceStartCodonToMethionine={model.get('coerceStartCodonToMethionine')}
          setCoerceStartCodonToMethionine={(shouldCoerceStartCodonToMethionine) =>
            model.set('coerceStartCodonToMethionine', shouldCoerceStartCodonToMethionine)
          }
        />,
        this.el,
        this.reactRoot
      );
    }
    return this;
  }

  override removeAsync(callback: () => void): this {
    unmountRootsAsync([this.reactRoot], () => {
      this.reactRoot = null;
      super.remove();
      callback();
    });
    return this;
  }
}

BiochemicalPropertiesTabView.prototype.className = 'buffer-tab buffer-tab-reset buffer-tab--no-padding';

export {
  BiochemicalPropertiesTabView,
  AaSequenceBiochemicalPropertiesTabState,
  TranslationBiochemicalPropertiesTabState,
};
